import axios from "axios";
import { useContext, useState, useEffect, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { Modal, ModalBody, Spinner } from "reactstrap";
import { IoClose } from "react-icons/io5";
import SpinnerGif from "../../assets/images/Spinner.gif";
import { _imagesURL, adminBaseURL, FollowApi } from "../../constants/API";
import { Language } from "../../App";
import { Link } from "react-router-dom";
import { PROFILE } from "../../constants/RoutesUrl";
import ImageComponent from "../common/ImageComponent";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import { authorize } from "../../shared/authorize";

function PollLikesModal({ toggleModal, modal, pollId }) {
  const { lang } = useContext(Language);

  const loggedInUser = (() => {
    try {
      const splash = JSON.parse(localStorage.getItem("SplashData"));
      const signup = JSON.parse(localStorage.getItem("SignupData"));
      return splash?.user?.id || signup?.data?.id;
    } catch (e) {
      return null;
    }
  })();

  const [hasMoreFollowing, setHasMoreFollowing] = useState(true);
  const [pagefollowing, setPagefollowing] = useState(1);
  const [list, setList] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Using useCallback to memoize the fetchData function
  const fetchData = useCallback(async (page) => {
    try {
      const response = await axios.get(
        `${adminBaseURL}user/post/poll/voters/${pollId}?page=${page}`,
        authorize()
      );
      const updatedValue = response.data.data.data;
      
      if (page === 1) {
        setList(updatedValue); // Replace list for first page
      } else {
        setList(prevData => [...prevData, ...updatedValue]); // Append for subsequent pages
      }

      // Update pagination state
      if (response.data.data.current_page >= response.data.data.last_page) {
        setHasMoreFollowing(false);
      } else {
        setPagefollowing(page + 1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsInitialLoading(false);
    }
  }, [pollId]);

  // Load initial data when modal opens
  useEffect(() => {
    if (modal) {
      setIsInitialLoading(true);
      setPagefollowing(1);
      setHasMoreFollowing(true);
      fetchData(1);
    } else {
      // Reset state when modal closes
      setList([]);
      setPagefollowing(1);
      setHasMoreFollowing(true);
      setIsInitialLoading(true);
    }
  }, [modal, fetchData]);

  const handleLoadMore = () => {
    if (!isInitialLoading && !isLoading) {
      fetchData(pagefollowing);
    }
  };

  const handleFollow = useCallback(
    (id) => {
      setIsLoading(true);
      axios
        .get(`${FollowApi}${id}`, authorize())
        .then((res) => {
          if (res.data.status) {
            setList((prevList) =>
              prevList.map((profile) =>
                profile.id === id
                  ? { ...profile, follow: !profile.follow }
                  : profile
              )
            );
          } else {
            console.log(res.data.error);
          }
        })
        .catch(console.error)
        .finally(() => {
          setIsLoading(false);
        });
    },
    []
  );

  const modalContentHeight = "400px";

  return (
    <Modal
      scrollable
      centered
      isOpen={modal}
      toggle={toggleModal}
      contentClassName="border-0 shadow-lg"
      style={{ borderRadius: "26px", overflow: "hidden" }}
    >
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
        style={{ background: "#FFFFFF", borderColor: "#E6E7EA" }}
      >
        <div>
          <h5 className="roboto-bold fs-16 mb-0" style={{ color: "#0F1A2B", fontWeight: 600 }}>
            {lang.voters || "Voters"}
          </h5>
          <span className="fs-12 roboto-regular" style={{ color: "#5F6673" }}>
            {lang.voters_list || "People who voted in this poll"}
          </span>
        </div>
        <button
          type="button"
          className="d-flex align-items-center justify-content-center flex-shrink-0 border-0 cursor"
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 2px 8px rgba(15, 26, 43, 0.16)",
          }}
          onClick={toggleModal}
        >
          <IoClose size={20} color="#0E1421" />
        </button>
      </div>

      <ModalBody className="p-4 scroll-hide" style={{ height: modalContentHeight, background: "#FFFFFF" }}>
        {isInitialLoading ? (
          <div className="w-100 text-center d-flex align-items-center justify-content-center h-100">
            <Spinner size="md" style={{ color: "#131722" }} />
          </div>
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={handleLoadMore}
            hasMore={hasMoreFollowing}
            loader={
              <div className="w-100 text-center py-3" key={0}>
                <img src={SpinnerGif} alt="..." width={24} height={24} />
              </div>
            }
            useWindow={false}
            height={modalContentHeight}
          >
            {list.length === 0 ? (
              <div className="d-flex align-items-center justify-content-center h-100">
                <p className="text-center roboto-medium fs-13" style={{ color: "#5F6673" }}>
                  {lang.no_voters_yet || "No voters yet"}
                </p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {list.map((value, index) => (
                  <div
                    className="d-flex align-items-center justify-content-between p-3"
                    key={index}
                    style={{
                      border: "1px solid #E6E7EA",
                      borderRadius: "14px",
                      background: "#FFFFFF",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Link
                      to={`${PROFILE}/${value.user_name || value.id}`}
                      className="d-flex align-items-center gap-3 text-decoration-none flex-grow-1"
                      style={{ minWidth: 0 }}
                    >
                      <div
                        className="rounded-circle overflow-hidden flex-shrink-0"
                        style={{
                          width: "44px",
                          height: "44px",
                          border: "1px solid #E6E7EA",
                        }}
                      >
                        <ImageComponent
                          src={value?.image ? _imagesURL + value.image : Avatar}
                          compClassName="w-100 h-100 object-cover rounded-circle"
                          loader_height="44px"
                          loader_width="44px"
                          img_width="44px"
                          img_height="44px"
                        />
                      </div>
                      <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                        <span className="fs-14 roboto-bold text-truncate" style={{ color: "#0F1A2B", fontWeight: 600 }}>
                          {value.name}
                        </span>
                        <span className="fs-12 roboto-regular text-truncate" style={{ color: "#5F6673" }}>
                          @{value.user_name}
                        </span>
                      </div>
                    </Link>
                    {String(value.id) !== String(loggedInUser) && (
                      <div className="flex-shrink-0 ms-2">
                        <button
                          disabled={isLoading}
                          onClick={() => handleFollow(value.id)}
                          className={`btn btn-sm rounded-pill px-3 py-1.5 cursor d-inline-flex align-items-center ${
                            value.follow ? "feed-following-btn" : "feed-follow-btn"
                          }`}
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          {value.follow ? (lang?.following || "Following") : (lang?.follow || "Follow")}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </InfiniteScroll>
        )}
      </ModalBody>
    </Modal>
  );
}

export default PollLikesModal;